import React, { useEffect, useState } from 'react';
import {
    Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, FormControlLabel, Grid, Radio, RadioGroup, TextField, Box,
} from '@mui/material';
import { trackActivity } from '../../../utils/mixpanel';
import { globalGetService, globalPostService } from '../../../utils/globalApiServices';
import { checkApiStatus, getGlobalCookie } from '../../../utils';
import { useSnackbar } from 'notistack';
import AutocompleteCreatable from '../../../shared_elements/components/AutocompleteCreatable';
import { regexConstants } from '../../../constants/regEx';
import { getEmailListApi, bulkSendInvoiceMailApi } from '../apiService';
import { avationPlcInstance } from '../../../shared_elements';

const generateDefaultSubject = () => 'Outstanding Invoices';

const MANAGEMENT_FEE_INVOICE_TYPE = 10;

const numberInvoices = (invoices) => invoices
    .map((inv, idx) => `${idx + 1}. ${inv.invoice_number || `Invoice ${idx + 1}`}`)
    .join('\n');

const groupInvoicesByAsset = (invoices) => {
    const groups = new Map();
    invoices.forEach((inv) => {
        const assetName = inv?.asset?.name?.trim() || "Other's";
        if (!groups.has(assetName)) {
            groups.set(assetName, []);
        }
        groups.get(assetName).push(inv);
    });
    return groups;
};

const generateManagementFeeContent = (invoices) => numberInvoices(invoices);

const generateAssetWiseContent = (invoices) => {
    const groups = groupInvoicesByAsset(invoices);
    const sections = Array.from(groups.entries()).map(
        ([assetName, groupInvoices]) => `${assetName}\n${numberInvoices(groupInvoices)}`
    );
    return sections.join('\n\n');
};

const generateDefaultContent = (invoices) => {
    const isManagementFee = invoices?.[0]?.invoice_type?.value === MANAGEMENT_FEE_INVOICE_TYPE;
    const body = isManagementFee ? generateManagementFeeContent(invoices) : generateAssetWiseContent(invoices);
    return `Please find the list of outstanding invoices below:\n\n${body}`;
};

const BulkSendMailModal = ({ selectedInvoices, onClose, onSuccess, getResponseBack }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setLoading] = useState(false);
    const [data, setData] = useState({
        to_emails_list: [],
        cc_emails_list: [],
        subject: generateDefaultSubject(),
        format: avationPlcInstance ? 'excel' : 'pdf',
        signature: null,
        content: generateDefaultContent(selectedInvoices),
    });
    const [error, setError] = useState({});
    const [dropDownLoader, setDropDownLoader] = useState({ user: false });
    const [user, setUser] = useState([]);
    const userinfo = JSON.parse(getGlobalCookie('lessorAccess'));

    useEffect(() => {
        getUserList();
    }, []);

    const getUserList = () => {
        const lesseeIds = selectedInvoices.map(inv => inv.lessee?.id).filter(Boolean).join(',');
        setDropDownLoader(prev => ({ ...prev, user: true }));
        globalGetService(getEmailListApi(lesseeIds ? { lessee_ids: lesseeIds } : {})).then(response => {
            setDropDownLoader(prev => ({ ...prev, user: false }));
            if (checkApiStatus(response)) {
                const emails = response?.data?.data || [];
                setUser(emails);
                setData(prev => ({ ...prev, to_emails_list: emails.map(email => ({ label: email })) }));
            }
        });
    };

    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: 'Bulk Send Invoice', page_type });
    };

    const onFieldChange = (key, value) => {
        if (Array.isArray(value)) {
            const unique = [];
            value.forEach(item => {
                const email = item?.label || item;
                if (!unique.includes(email) && regexConstants.email.test(email)) {
                    unique.push(email);
                }
            });
            setData(prev => ({ ...prev, [key]: unique.map(e => ({ label: e })) }));
        } else {
            setData(prev => ({ ...prev, [key]: value }));
        }
    };

    const onRestErrorKey = (key) => {
        setError(prev => ({ ...prev, [key]: '' }));
    };

    const onSendMail = () => {
        let validationInputs = {
            subject: data?.subject ? '' : 'A subject line is required so the recipient knows what this email is about',
            content: data?.content ? '' : 'Write a message to accompany the invoice before sending',
            to_emails_list: '',
            cc_emails_list: '',
        };

        if (!data?.to_emails_list || data?.to_emails_list?.length === 0) {
            validationInputs.to_emails_list = 'Enter at least one recipient email address to send the invoice';
        } else {
            const invalidEmails = data.to_emails_list.filter(
                item => !regexConstants.email.test(item?.label || item)
            );
            if (invalidEmails.length > 0) {
                validationInputs.to_emails_list = 'Please enter valid Email(s)';
                enqueueSnackbar(
                    `The following email address(es) are invalid: ${invalidEmails.map(e => e?.label || e).join(', ')}`,
                    { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } }
                );
            }
        }

        if (data?.cc_emails_list?.length) {
            const invalidCcEmails = data.cc_emails_list.filter(
                item => !regexConstants.email.test(item?.label || item)
            );
            if (invalidCcEmails.length > 0) {
                validationInputs.cc_emails_list = 'Please enter valid CC Email(s)';
                enqueueSnackbar(
                    `The following CC email address(es) are invalid: ${invalidCcEmails.map(e => e?.label || e).join(', ')}`,
                    { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } }
                );
            }
        }

        if (Object.keys(validationInputs).every(k => validationInputs[k] === '')) {
            const payload = {
                invoice_ids: selectedInvoices.map(inv => inv.id).filter(Boolean),
                to_emails_list: data?.to_emails_list?.map(item => item?.label || item) || [],
                cc_emails_list: data?.cc_emails_list?.map(item => item?.label || item) || [],
                subject: data?.subject || null,
                content: data?.content || null,
                format: data?.format || 'pdf',
                signature: data?.signature || null,
                shared_by: userinfo?.id || null,
            };
            setLoading(true);
            globalPostService(bulkSendInvoiceMailApi(), payload).then(response => {
                setLoading(false);
                if (checkApiStatus(response)) {
                    enqueueSnackbar(response?.data?.message || 'Invoices sent successfully', {
                        variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' },
                    });
                    trackActivity('Bulk Invoice Sent', {
                        page_title: 'Fleet Invoices',
                        total: selectedInvoices.length,
                    });
                    // getResponseBack();
                    onSuccess?.();
                    onClose();
                } else {
                    enqueueSnackbar(response?.data?.message || 'Failed to send invoices', {
                        variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' },
                    });
                }
            });
        } else {
            setError(validationInputs);
        }
    };

    return (
        <Dialog
            aria-labelledby="bulk-send-mail-dialog-title"
            className="add-lease-dialog"
            maxWidth="md"
            fullWidth
            open
        >
            <DialogTitle
                sx={{ m: 0, p: 2, pl: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                className="dialog-header"
                id="bulk-send-mail-dialog-title"
            >
                Bulk Send Invoice
                <Box
                    sx={{
                        bgcolor: '#f1f5f9',
                        color: '#475569',
                        px: 1.2,
                        py: 0.3,
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 600,
                    }}
                >
                    {selectedInvoices.length} Invoice{selectedInvoices.length > 1 ? 's' : ''}
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2}>
                    {/* To */}
                    <Grid size={{ md: 12, xs: 12 }}>
                        <Box sx={{ position: 'relative' }}>
                            <AutocompleteCreatable
                                options={user
                                    ?.filter(email => !data.to_emails_list?.some(i => (i.label || i) === email))
                                    ?.map(email => ({ label: email }))}
                                paramsKey="to_emails_list"
                                optionKey="label"
                                label="To"
                                value={data?.to_emails_list || []}
                                onFieldChange={(e, paramsKey, newValue) => onFieldChange('to_emails_list', newValue)}
                                required
                                multiple
                                isDisabled={dropDownLoader?.user}
                                noOptionsText="No options, please type to create new"
                                placeholder={data?.to_emails_list?.length === 0 ? 'Enter Email(s)' : ''}
                                error={error?.to_emails_list}
                                helperText={error?.to_emails_list || ''}
                                resetErrorKey={() => onRestErrorKey('to_emails_list')}
                            />
                            {dropDownLoader?.user && (
                                <CircularProgress
                                    size={16}
                                    sx={{ position: 'absolute', right: 40, top: 14, color: 'text.secondary' }}
                                />
                            )}
                        </Box>
                    </Grid>

                    {/* CC */}
                    <Grid size={{ md: 12, xs: 12 }}>
                        <AutocompleteCreatable
                            options={user
                                ?.filter(email => !data.cc_emails_list?.some(i => (i.label || i) === email))
                                ?.map(email => ({ label: email }))}
                            paramsKey="cc_emails_list"
                            optionKey="label"
                            label="CC"
                            value={data?.cc_emails_list || []}
                            onFieldChange={(e, paramsKey, newValue) => onFieldChange('cc_emails_list', newValue)}
                            required={false}
                            multiple
                            noOptionsText={dropDownLoader?.user ? 'loading...' : 'No options, please type to create new'}
                            placeholder={data?.cc_emails_list?.length === 0 ? 'Enter CC Email(s)' : ''}
                            error={error?.cc_emails_list}
                            helperText={error?.cc_emails_list || ''}
                            resetErrorKey={() => onRestErrorKey('cc_emails_list')}
                        />
                    </Grid>

                    {/* Subject */}
                    <Grid size={{ md: 12, xs: 12 }}>
                        <TextField
                            id="bulk-send-subject"
                            label="Subject"
                            variant="outlined"
                            size="small"
                            required
                            value={data?.subject || ''}
                            onChange={e => onFieldChange('subject', e.target.value.trimStart())}
                            onFocus={() => onRestErrorKey('subject')}
                            margin="normal"
                            fullWidth
                            slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } }}
                            error={!!error?.subject}
                            helperText={error?.subject}
                        />
                    </Grid>

                    {/* Content */}
                    <Grid size={{ md: 12, xs: 12 }}>
                        <TextField
                            id="bulk-send-content"
                            label="Content"
                            variant="outlined"
                            size="small"
                            margin="normal"
                            required
                            value={data?.content || ''}
                            onChange={e => onFieldChange('content', e.target.value.trimStart())}
                            onFocus={() => onRestErrorKey('content')}
                            slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 1000 } }}
                            sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
                            fullWidth
                            multiline
                            rows={4}
                            error={!!error?.content}
                            helperText={error?.content}
                        />
                    </Grid>

                    {/* Format */}
                    <Grid item md={12} xs={12}>
                        <FormControl>
                            <RadioGroup
                                row
                                aria-labelledby="bulk-send-format-label"
                                name="file-format"
                                defaultValue={avationPlcInstance ? 'excel' : 'pdf'}
                                value={data?.format || (avationPlcInstance ? 'excel' : 'pdf')}
                                className="radio-color"
                            >
                                <FormControlLabel
                                    value="pdf"
                                    checked={data?.format === 'pdf'}
                                    control={<Radio size="small" onChange={() => onFieldChange('format', 'pdf')} />}
                                    label="PDF Format"
                                />
                                <FormControlLabel
                                    value="excel"
                                    checked={data?.format === 'excel'}
                                    control={<Radio size="small" onChange={() => onFieldChange('format', 'excel')} />}
                                    label="Excel Format"
                                />
                                <FormControlLabel
                                    value="docs"
                                    checked={data?.format === 'docs'}
                                    control={<Radio size="small" onChange={() => onFieldChange('format', 'docs')} />}
                                    label="Docs Format"
                                />
                                {/* <FormControlLabel
                                    value="csv"
                                    checked={data?.format === 'csv'}
                                    control={<Radio size="small" onChange={() => onFieldChange('format', 'csv')} />}
                                    label="CSV Format"
                                /> */}
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    {/* Email Signature */}
                    <Grid size={{ md: 12, xs: 12 }}>
                        <TextField
                            id="bulk-send-email-signature"
                            label="Email Signature"
                            variant="outlined"
                            size="small"
                            margin="normal"
                            value={data?.signature || ''}
                            onChange={e => onFieldChange('signature', e.target.value.trimStart())}
                            slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 500 } }}
                            fullWidth
                            sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
                            multiline
                            rows={3}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ marginRight: '15px' }}>
                <Button
                    className="cancel-btn"
                    variant="outlined"
                    size="small"
                    disabled={isLoading}
                    onClick={() => { onClose(); handleItemClicked('Cancel Button'); }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={isLoading}
                    className="btn-primary"
                    size="small"
                    onClick={() => { onSendMail(); handleItemClicked('Send Button'); }}
                >
                    {isLoading ? <CircularProgress size={24} /> : 'Send'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkSendMailModal;
